
import React, { useState, useEffect } from 'react';
import { BottomAppShowcase, PageShell } from './public/PublicPrimitives';
import { CleroS, CleroWordmarkSvg } from './Logo';
import Logo from './Logo';
import { FaApple, FaAndroid } from 'react-icons/fa';
import Lenis from 'lenis';
import { ANDROID_INVITE_URL, IOS_INVITE_URL } from '../constants/downloadLinks';

export { ANDROID_INVITE_URL, IOS_INVITE_URL };

const TermsPage: React.FC = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const lastUpdated = 'October 13, 2026';

  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.0,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      orientation: 'vertical',
      smoothWheel: true,
    });

    const handleScroll = () => {
      setIsScrolled(window.scrollY > 30);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    let rafId: number;
    function raf(time: number) {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    }
    rafId = requestAnimationFrame(raf);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      cancelAnimationFrame(rafId);
      lenis.destroy();
    };
  }, []);

  const navigate = (path: string) => {
    window.history.pushState({}, '', path);
    window.dispatchEvent(new PopStateEvent('popstate'));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <PageShell
      background="bg-[#fafaf7]"
      description="Read the Terms of Service for Clero."
      title="Terms of Service | Clero"
    >
      {/* FLOATING / MORPHING HEADER */}
      <div
        className={`fixed top-0 left-0 right-0 z-50 flex justify-center pointer-events-none transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] ${
          isScrolled ? 'pt-3.5 px-4 sm:px-6' : 'pt-0 px-0'
        }`}
      >
        <header
          className={`pointer-events-auto w-full transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] ${
            isScrolled
              ? 'max-w-[480px] rounded-[999px] bg-white/85 backdrop-blur-2xl border border-slate-200/80 px-4 sm:px-5 py-2 shadow-sm'
              : 'max-w-[720px] sm:max-w-[760px] rounded-[0px] bg-transparent backdrop-blur-none border-b border-slate-200/50 px-4 sm:px-6 py-3.5'
          }`}
        >
          <div className="flex items-center justify-between gap-4 sm:gap-6">
            <div className="flex items-center gap-2 shrink-0">
              <a
                aria-label="Clero Home"
                className="flex items-center transition opacity-95 hover:opacity-100"
                href="/"
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/');
                }}
              >
                <CleroWordmarkSvg height={24} color="#0D9488" />
              </a>
            </div>

            <div className="flex items-center gap-2.5 sm:gap-4 shrink-0">
              <button
                className="text-xs sm:text-sm font-bold text-slate-700 hover:text-slate-950 transition-colors whitespace-nowrap px-1 py-1"
                onClick={() => navigate('/support')}
                type="button"
              >
                Help Center
              </button>

              <button
                className="inline-flex items-center justify-center rounded-full bg-[var(--clero-primary)] px-3.5 sm:px-4 py-1.5 sm:py-2 text-xs font-bold text-white transition-all hover:bg-[var(--clero-primary-dark)] shadow-sm active:scale-95 whitespace-nowrap"
                onClick={() => {
                  const el = document.getElementById('bottom-download');
                  if (el) {
                    el.scrollIntoView({ behavior: 'smooth' });
                  }
                }}
                type="button"
              >
                Download App
              </button>
            </div>
          </div>
        </header>
      </div>

      <div className="h-16 sm:h-20" />

      {/* HERO SECTION */}
      <section className="pt-8 pb-10 sm:pb-14 px-4 sm:px-6">
        <div className="mx-auto max-w-3xl text-center space-y-3">
          <span className="text-xs font-extrabold uppercase tracking-widest text-teal-600 block">
            Legal & Terms
          </span>
          <h1 className="text-4xl sm:text-6xl font-black tracking-tight text-slate-950">
            Terms of Service
          </h1>
          <p className="text-xs font-bold text-slate-400 uppercase tracking-wider pt-1">
            Last updated: {lastUpdated}
          </p>
        </div>
      </section>

      {/* MAIN CONTENT */}
      <section className="py-6 px-4 sm:px-6">
        <div className="mx-auto max-w-3xl space-y-6">

          <div className="rounded-3xl bg-white p-8 sm:p-10 space-y-4">
            <p className="text-base sm:text-lg font-medium text-slate-900 leading-relaxed">
              By using Clero, you agree to these Terms.
            </p>
          </div>

          <div className="rounded-3xl bg-white p-8 sm:p-10 space-y-4">
            <h2 className="text-xl font-bold text-slate-950 tracking-tight">What Clero Does</h2>
            <p className="text-sm sm:text-base text-slate-600 font-medium leading-relaxed">
              Clero helps coordinate and facilitate split payments between individuals. Clero is not a bank, lender, or escrow service.
            </p>
          </div>

          <div className="rounded-3xl bg-white p-8 sm:p-10 space-y-4">
            <h2 className="text-xl font-bold text-slate-950 tracking-tight">Agreement, Eligibility & Account Security</h2>
            <p className="text-sm sm:text-base text-slate-600 font-medium leading-relaxed">
              By accessing or using Clero, you agree to these Terms and any disclosures shown in the service. You must provide accurate information, protect your account and authentication methods, use Clero only for lawful purposes, and reside in the United States while Clero remains US-only. You are responsible for activity performed through your account except where prohibited by law.
            </p>
          </div>

          <div className="rounded-3xl bg-white p-8 sm:p-10 space-y-4">
            <h2 className="text-xl font-bold text-slate-950 tracking-tight">Coordination Fees</h2>
            <ul className="list-disc pl-5 space-y-2 text-sm sm:text-base text-slate-600 font-medium">
              <li>Payees currently pay no Clero fee for split payments.</li>
              <li>Organizer may be charged 1.0% of amount owed to them, capped at $2.99 per split session.</li>
              <li>Organizer coordination fee applies only when two or more participants owe in split.</li>
            </ul>
          </div>

          <div className="rounded-3xl bg-white p-8 sm:p-10 space-y-4">
            <h2 className="text-xl font-bold text-slate-950 tracking-tight">Payments & Lawful Use</h2>
            <p className="text-sm sm:text-base text-slate-600 font-medium leading-relaxed">
              Payments are processed by third-party providers. Clero does not hold funds or store sensitive payment credentials. Provider eligibility, identity verification, bank processing windows, limits, outages, and separate provider terms may affect availability and timing. Estimated arrival dates are not guarantees.
            </p>
          </div>

          <div className="rounded-3xl bg-white p-8 sm:p-10 space-y-4">
            <h2 className="text-xl font-bold text-slate-950 tracking-tight">Clero Rewards</h2>
            <p className="text-sm sm:text-base text-slate-600 font-medium leading-relaxed">
              Clero Rewards provides promotional cash-back offers on qualifying purchases. Rewards are not guaranteed and are subject to these Terms, the details shown for each offer, merchant and payment-network data, and any additional disclosures presented before activation.
            </p>
            <ul className="list-disc pl-5 space-y-2 text-sm sm:text-base text-slate-600 font-medium">
              <li><strong>Activation:</strong> Unless an offer says otherwise, you must activate it before making the purchase and use any connected card that Clero can identify through your linked account.</li>
              <li><strong>Qualification:</strong> A purchase must be posted during the offer period, match the participating merchant, meet any minimum purchase amount, and comply with redemption limits and offer-specific terms. Pending authorizations do not qualify until posted.</li>
              <li><strong>Merchant recognition:</strong> Eligibility depends on transaction information supplied by merchants, Plaid, payment networks, and other providers. Purchases processed under a different merchant name, through a marketplace, digital wallet, payment facilitator, third-party delivery service, or reseller may not be recognized.</li>
              <li><strong>Excluded amounts:</strong> Unless an offer expressly states otherwise, gift cards, cash equivalents, peer-to-peer transfers, payment-service transactions, fees, returns, refunds, reversals, fraudulent transactions, and other non-purchase activity do not qualify. Taxes, tips, shipping, and other charges qualify only when included in the posted transaction and permitted by the offer terms.</li>
              <li><strong>Reward calculation:</strong> Cash back is calculated from the qualifying posted amount, subject to the stated rate, maximum reward, and redemption limit. Clero's records and provider data govern the calculation, subject to correction of errors.</li>
            </ul>
          </div>

          <div className="rounded-3xl bg-white p-8 sm:p-10 space-y-4">
            <h2 className="text-xl font-bold text-slate-950 tracking-tight">Reward Confirmation, Payment & Reversals</h2>
            <ul className="list-disc pl-5 space-y-2 text-sm sm:text-base text-slate-600 font-medium">
              <li>Rewards may remain pending while a transaction posts, is confirmed, or is reviewed for eligibility, refunds, fraud, or abuse.</li>
              <li>Confirmed rewards may be deposited to your default connected bank account. Identity verification, an eligible deposit account, and other payment-provider requirements may apply.</li>
              <li>If a direct deposit cannot be completed, Clero may place the reward in a claimable Clero balance until you complete the required setup or choose an available destination.</li>
              <li>Estimated arrival dates are estimates, not guarantees. Banks and payment providers control final processing times.</li>
              <li>If a qualifying purchase is returned, refunded, reversed, disputed, canceled, duplicated, or later found ineligible, Clero may cancel, withhold, reverse, or recover the related reward, including by offsetting future rewards where permitted by law.</li>
              <li>You are responsible for any taxes associated with rewards and for determining whether reporting is required.</li>
            </ul>
          </div>

          <div className="rounded-3xl bg-white p-8 sm:p-10 space-y-4">
            <h2 className="text-xl font-bold text-slate-950 tracking-tight">Offer Availability, Changes & Abuse</h2>
            <ul className="list-disc pl-5 space-y-2 text-sm sm:text-base text-slate-600 font-medium">
              <li>Offers may be targeted, limited, modified, suspended, or withdrawn at any time, subject to applicable law. Availability may vary by user, merchant, location, card, account status, or funding capacity.</li>
              <li>Offers have no cash value before a reward is confirmed and are non-transferable unless Clero expressly states otherwise.</li>
              <li>You may not manipulate transactions, create duplicate accounts, make sham purchases, cycle purchases and returns, or otherwise attempt to obtain rewards through fraud or abuse.</li>
              <li>Clero may delay or deny a reward, suspend Rewards access, or close an account when reasonably necessary to investigate suspected error, fraud, misuse, sanctions, or a violation of these Terms.</li>
              <li>Participating merchants do not sponsor, administer, or endorse Clero Rewards unless an offer expressly says so. Questions and disputes about Clero Rewards should be directed to Clero, not the merchant.</li>
            </ul>
            <p className="text-sm sm:text-base text-slate-600 font-medium leading-relaxed">
              Contact <a href="mailto:support@clero.money" className="text-teal-700 font-bold hover:underline">support@clero.money</a> promptly if you believe a reward was calculated incorrectly. Include the offer, merchant, purchase date, and amount. We may request reasonable supporting information and may be unable to investigate claims submitted more than 90 days after the purchase or reward decision.
            </p>
          </div>

          <div className="rounded-3xl bg-white p-8 sm:p-10 space-y-4">
            <h2 className="text-xl font-bold text-slate-950 tracking-tight">Connected Email Integrations (Gmail)</h2>
            <p className="text-sm sm:text-base text-slate-600 font-medium leading-relaxed">
              If you choose to connect Gmail, you authorize Clero to use read-only Gmail access to identify likely receipt emails and provide split suggestions.
            </p>
            <ul className="list-disc pl-5 space-y-2 text-sm sm:text-base text-slate-600 font-medium">
              <li>Clero does not send, modify, or delete your emails.</li>
              <li>Clero uses Gmail-derived data only for receipt suggestion and split-related functionality.</li>
              <li>You may disconnect Gmail at any time from the app settings.</li>
            </ul>
            <p className="text-sm sm:text-base text-slate-600 font-medium leading-relaxed">
              By connecting Gmail, you acknowledge this limited-use processing described in our Privacy Policy.
            </p>
          </div>

          <div className="rounded-3xl bg-white p-8 sm:p-10 space-y-4">
            <h2 className="text-xl font-bold text-slate-950 tracking-tight">Artificial Intelligence & Automated Features</h2>
            <p className="text-sm sm:text-base text-slate-600 font-medium leading-relaxed">
              Optional AI tools (powered by Google Gemini / Vertex AI APIs) help extract receipt items, parse email receipts, look up merchant details, and format invitation text.
            </p>
            <ul className="list-disc pl-5 space-y-2 text-sm sm:text-base text-slate-600 font-medium">
              <li><strong>Suggestions & Accuracy:</strong> AI outputs (extracted totals, line items, tax, and tip) are suggestions provided for convenience and may contain inaccuracies. You remain responsible for inspecting and verifying all amounts prior to sending payment requests or confirming splits.</li>
              <li><strong>No Model Training:</strong> Data submitted to AI features is used solely to deliver feature responses to you and is not used to train third-party foundation models.</li>
              <li><strong>Third-Party Provider Policy:</strong> When using AI features, input data is processed via Google Gemini APIs. See <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-teal-700 font-bold hover:underline">Google’s Privacy Policy</a> for how they handle API data.</li>
            </ul>
          </div>

          <div className="rounded-3xl bg-white p-8 sm:p-10 space-y-4">
            <h2 className="text-xl font-bold text-slate-950 tracking-tight">SMS Messaging Terms</h2>
            <p className="text-sm sm:text-base text-slate-600 font-medium leading-relaxed">
              By providing your phone number to Clero, you agree to receive transactional SMS messages related to your account, including verification codes, payment notifications, and important service updates.
            </p>
            <p className="text-sm sm:text-base text-slate-600 font-medium leading-relaxed">
              Message frequency varies. Message and data rates may apply.<br />
              For assistance, contact <a href="mailto:support@clero.money" className="text-teal-700 font-bold hover:underline">support@clero.money</a>.
            </p>
          </div>

          <div className="rounded-3xl bg-white p-8 sm:p-10 space-y-4">
            <h2 className="text-xl font-bold text-slate-950 tracking-tight">User Responsibilities</h2>
            <p className="text-sm sm:text-base text-slate-600 font-medium leading-relaxed">You agree to:</p>
            <ul className="list-disc pl-5 space-y-2 text-sm sm:text-base text-slate-600 font-medium">
              <li>Provide accurate information</li>
              <li>Use Clero only for lawful purposes</li>
              <li>Reside in the United States (Clero is currently US-only)</li>
            </ul>
          </div>

          <div className="rounded-3xl bg-white p-8 sm:p-10 space-y-4">
            <h2 className="text-xl font-bold text-slate-950 tracking-tight">Limitation of Liability</h2>
            <p className="text-sm sm:text-base text-slate-600 font-medium leading-relaxed">
              Clero is provided “as is” and “as available” to the fullest extent permitted by law. Clero does not guarantee uninterrupted availability, merchant recognition, reward eligibility, payment timing, or error-free automated results. To the fullest extent permitted by law, Clero and Oryn Technologies Inc. are not liable for indirect, incidental, special, consequential, exemplary, or punitive damages, or losses caused by third-party providers, merchants, financial institutions, or events outside reasonable control. Nothing in these Terms limits rights or liability that cannot legally be limited.
            </p>
          </div>

          <div className="rounded-3xl bg-white p-8 sm:p-10 space-y-4">
            <h2 className="text-xl font-bold text-slate-950 tracking-tight">Termination</h2>
            <p className="text-sm sm:text-base text-slate-600 font-medium leading-relaxed">
              You may stop using Clero at any time. We may restrict or suspend access for a violation of these Terms, suspected fraud or abuse, legal or provider requirements, security risk, or protection of users and the service. Terms that by their nature should survive termination remain in effect.
            </p>
          </div>

          <div className="rounded-3xl bg-white p-8 sm:p-10 space-y-4">
            <h2 className="text-xl font-bold text-slate-950 tracking-tight">Changes to These Terms</h2>
            <p className="text-sm sm:text-base text-slate-600 font-medium leading-relaxed">
              Clero may update these Terms periodically. Material changes will be communicated as required by law. Continued use after an effective update constitutes acceptance where permitted by law.
            </p>
          </div>

          <div className="rounded-3xl bg-white p-8 sm:p-10 space-y-4">
            <h2 className="text-xl font-bold text-slate-950 tracking-tight">Contact</h2>
            <p className="text-sm sm:text-base text-slate-600 font-medium leading-relaxed">
              For support questions, contact:<br />
              <a href="mailto:support@clero.money" className="text-teal-700 font-bold hover:underline">support@clero.money</a>
            </p>
          </div>

          <div className="text-center py-4">
            <p className="text-xs text-slate-400 font-medium">These Terms may be updated periodically.</p>
          </div>

        </div>
      </section>

      <div className="mt-12 mx-auto max-w-[var(--clero-max-content)] px-4 sm:px-6 lg:px-8 mb-12">
        <BottomAppShowcase />
      </div>

      {/* FOOTER (MATCHING MAIN LANDING FOOTER) */}
      <footer className="border-t border-slate-200/80 bg-white px-4 py-12 sm:px-6 lg:px-8 mt-16">
        <div className="mx-auto max-w-[var(--clero-max-content)]">
          <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-5">
            <div className="space-y-4 sm:col-span-2 lg:col-span-1">
              <img src="/clero_icon.png" alt="Clero" className="h-14 w-14 sm:h-16 sm:w-16 rounded-2xl shadow-sm object-cover" />
              <p className="text-xs font-semibold text-slate-400">
                Secured with direct bank connections via Plaid & Astra Financial.
              </p>
            </div>

            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-slate-900">Compare</p>
              <ul className="mt-3 space-y-2 text-xs font-semibold text-slate-600">
                <li>
                  <button className="transition hover:text-teal-700" onClick={() => navigate('/compare')} type="button">
                    All Splitting Apps
                  </button>
                </li>
                <li>
                  <button className="transition hover:text-teal-700" onClick={() => navigate('/compare/splitwise')} type="button">
                    Clero vs Splitwise
                  </button>
                </li>
                <li>
                  <button className="transition hover:text-teal-700" onClick={() => navigate('/compare/venmo')} type="button">
                    Clero vs Venmo
                  </button>
                </li>
                <li>
                  <button className="transition hover:text-teal-700" onClick={() => navigate('/compare/zelle')} type="button">
                    Clero vs Zelle
                  </button>
                </li>
              </ul>
            </div>

            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-slate-900">Product</p>
              <ul className="mt-3 space-y-2 text-xs font-semibold text-slate-600">
                <li><button className="transition hover:text-slate-950" onClick={() => navigate('/')} type="button">Features</button></li>
                <li><a className="transition hover:text-slate-950" href={IOS_INVITE_URL} target="_blank" rel="noopener noreferrer">Download for iOS</a></li>
                <li><a className="transition hover:text-slate-950" href={ANDROID_INVITE_URL} target="_blank" rel="noopener noreferrer">Download for Android</a></li>
              </ul>
            </div>

            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-slate-900">Resources</p>
              <ul className="mt-3 space-y-2 text-xs font-semibold text-slate-600">
                <li><button className="transition hover:text-slate-950" onClick={() => navigate('/blog')} type="button">Blog</button></li>
                <li><button className="transition hover:text-slate-950" onClick={() => navigate('/support')} type="button">Help & Support</button></li>
                <li><button className="transition hover:text-slate-950" onClick={() => navigate('/compare')} type="button">Compare Apps</button></li>
              </ul>
            </div>

            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-slate-900">Legal & Privacy</p>
              <ul className="mt-3 space-y-2 text-xs font-semibold text-slate-600">
                <li><button className="transition hover:text-slate-950" onClick={() => navigate('/privacy')} type="button">Privacy Policy</button></li>
                <li><button className="text-teal-700 font-bold" onClick={() => navigate('/terms')} type="button">Terms of Service</button></li>
                <li><button className="transition hover:text-slate-950" onClick={() => navigate('/data-deletion')} type="button">Data Deletion</button></li>
              </ul>
            </div>
          </div>

          <div className="mt-10 border-t border-slate-100 pt-6 text-center text-xs text-slate-400">
            <p>© 2026 Oryn Technologies Inc. All rights reserved. Clero is a financial technology platform, not a bank.</p>
          </div>
        </div>
      </footer>
    </PageShell>
  );
};

export default TermsPage;

